import React, { useMemo } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import { Route, Switch } from 'react-router'
import LayoutPage from 'src/component-layout/LayoutPage/LayoutPage'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import NotFoundBackgroundPage from 'src/components/NotFoundBackgroundPage/NotFoundBackgroundPage'
import NotFoundBackgroundPageContent from 'src/components/NotFoundBackgroundPageContent/NotFoundBackgroundPageContent'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { useExtendLocation } from 'src/hooks/locationHooks'
import { useTranslate } from 'src/localization/Trns'
import { UserFeature } from 'src/models/user.models'
import { AppLocation } from 'src/navigation/appLocations'
import AutomationSettingsAppRoute from 'src/navigation/appRoutes/organization/automationSettingsAppRoute'
import { Rdrct } from 'src/navigation/Lnk'
import { automationSettingsRouteProps } from 'src/navigation/routeProps'
import { automationRulesUrl, expenseSettingsUrl } from 'src/navigation/urls'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import { selectOrganizationIdFromRoute } from 'src/redux/selectors/data/organizations.selectors'
import { selectLoadedFromRemoteConfigState } from 'src/redux/selectors/data/remoteConfig.selectors'
import { selectCalculatedBackgroundLocation } from 'src/redux/selectors/router.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'

const AutomationSettings: React.FC<React.PropsWithChildren<unknown>> = () => {
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )

  const translate = useTranslate()

  const organizationId = useSelector(selectOrganizationIdFromRoute)

  const extendLocation = useExtendLocation()
  const currentUser = useCurrentUser()
  const loadedRemoteConfig = useSelector(selectLoadedFromRemoteConfigState)
  const appName = useSelector(selectAppName)

  const { VIEW_AUTOMATION_RULES_SECTION: canViewAutomationRules } =
    useQueryUserFeatures([UserFeature.VIEW_AUTOMATION_RULES_SECTION]).data || {}

  const automationSubnavOptions = useMemo(
    () => [
      ...(canViewAutomationRules
        ? [
            {
              label: translate('Accounting Rules'),
              to: automationRulesUrl(
                organizationId !== undefined ? organizationId : '',
              ),
              isActive:
                extendLocation.location ===
                AppLocation.OrganizationSettingsAutomationRules,
              ariaLabelSource: translate('Accounting Rules'),
              'data-testid': 'subnavAutomation',
            },
          ]
        : []),
      {
        label: translate('Report Cycle'),
        to: expenseSettingsUrl(
          organizationId !== undefined ? organizationId : '',
        ),
        isActive:
          extendLocation.location ===
          AppLocation.OrganizationSettingsExpenseSettings,
        ariaLabelSource: 'Report Cycle',
        'data-testid': 'subnavReportCycle',
      },
    ],
    [
      extendLocation,
      organizationId,
      canViewAutomationRules,
      currentUser?.hasOrgAdminPermissions,
      canViewAutomationRules,
    ],
  )

  if (!loadedRemoteConfig) {
    return <LoadingBackgroundPage />
  }

  if (!currentUser?.hasOrgBookkeeperPermissions) {
    return <NotFoundBackgroundPage />
  }

  return (
    <>
      <Helmet>
        <title>{`Automation Settings | ${appName}`}</title>
        <meta
          name="description"
          content={`Automation Settings page of ${appName}`}
        />
      </Helmet>
      <LayoutPage
        subnavOptions={automationSubnavOptions}
        title={<span>{translate('Automation')}</span>}
      >
        <Switch location={calculatedBackgroundLocation}>
          {AutomationSettingsAppRoute.subRoutes.map((route, i) => (
            <Route key={i} {...route.routeProps}>
              {route.component}
            </Route>
          ))}
          <Route {...automationSettingsRouteProps} exact>
            <Rdrct
              to={automationRulesUrl(
                organizationId !== undefined ? organizationId : '',
              )}
            />
          </Route>
          <Route path={'/'}>
            <NotFoundBackgroundPageContent />
          </Route>
        </Switch>
      </LayoutPage>
    </>
  )
}

export default AutomationSettings
